import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Field from "../core/ui/forms/field";
import DualSelect from "partials/dual-select";
import SessionContext from "SessionContext";
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Form, FormGroup, Label, PopoverBody, Popover, Modal, ModalHeader, ModalBody, ModalFooter, Alert } from "reactstrap";
import ConfirmSuccess from "../core/ui/confirm-success";
import { validateEmail } from "core/utils/validate-email-format";
import ProgressLoader from "../partials/progress-loader";
import isEqual from "lodash.isequal";
import UnsavedChanges from "partials/unsaved-changes";

const user_init = {
  id: "",
  first_name: "",
  last_name: "",
  email: "",
  is_superuser: false,
  is_disabled: false,
  access_list_id: "",
  data_permissions: []
};

export default function UserForm(props) {
  const session = useContext(SessionContext);

  const history = useHistory();

  const [user, setUser] = useState(user_init);

  const [errors, setErrors] = useState({
    id: "",
    first_name: "",
    last_name: "",
    email: "",
    is_superuser: "",
    is_disabled: "",
    access_list_id: ""
  });

  const [accessLists, setAccessLists] = useState([]);
  const [allDataPermissions, setAllDataPermissions] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [noAdminUserRoles, setNoAdminUserRoles] = useState([]);
  const [selectedUserRole, setSelectedUserRole] = useState(null);
  const [selectedUserRoleID, setSelectedUserRoleID] = useState(null);
  const [selectedUserAccessID, setSelectedUserAccessID] = useState(null);
  const [selectedUserAccess, setSelectedUserAccess] = useState(null);
  const [selectedDataPermissions, setSelectedDataPermissions] = useState([]);
  const [selectUserRoleIsOpen, setSelectedUserRoleIsOpen] = useState(false);
  const [selectUserAccessIsOpen, setSelectedUserAccessIsOpen] = useState(false);
  const [disableEmail, setDisableEmail] = useState(false);
  const [showEmailPopover, setShowEmailPopover] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [prevUserInfo, setPrevUserInfo] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [showFirstNameMessage, setShowFirstNameMessage] = useState(false);
  const [showLastNameMessage, setShowLastNameMessage] = useState(false);
  const [showEmailMessage, setShowEmailMessage] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasInternalEmail, setHasInternalEmail] = useState(false);
  const [showResetMfaModal, setShowResetMfaModal] = useState(false);
  const [disableMfaButton, setDisableMfaButton] = useState(false);
  const [showMfaSuccess, setShowMfaSuccess] = useState(false);
  const [failToLoadUser, setFailToLoadUser] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [allowNavigateAway, setAllowNavigateAway] = useState(false);

  function updateDataPermissionsValue(ev, id) {
    setSelectedDataPermissions((prevValue) => {
      const field = prevValue.find((f) => f.id === id);
      field["value"] = ev.target.value;
      return [...prevValue];
    });
  }

  useEffect(() => {
    if (user.data_permissions && allDataPermissions) {
      setSelectedDataPermissions(
        user.data_permissions.map((cp) => {
          const a =
            allDataPermissions.find((p) => cp.data_permission_id === p.id) ||
            {};
          return { ...a, value: cp.value };
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDataPermissions]);

  // Match the user role ID to a value from the list for the dropdown
  useEffect(() => {
    if (!userRoles.length || !user.role) { return; }
    let role = userRoles.find((role) => role.id === user.role.id);
    role.name && setSelectedUserRole(role.name);
  }, [user.role, userRoles, selectedUserRoleID]);

  // Match the user access ID to a value from the list for the dropdown
  useEffect(() => {
    if (!accessLists.length || !user.access_list_id ) { return; }
    let access = accessLists.find((a) => {
      return a.id === user.access_list_id;
    });
    access && access.name && setSelectedUserAccess(access.name);
  }, [user.access_list_id, accessLists, selectedUserAccessID]);

  // Main API calls on page load
  useEffect(() => {
    const fetchAllUserData = async () => {
      setIsLoading(true);
      try {
        const user_id = props.args.user_id;
        if (user_id) {
          setIsNewUser(false);
          
          // User data call
          const _user = await session.query("users/get", { id: user_id });
          
          setUser(_user);
          setPrevUserInfo(_user);
          setDisableEmail(true);
          setSelectedUserRoleID(_user.role.id);
          setSelectedUserRole(_user.role.name);
          setSelectedUserAccessID(_user.access_list_id);
          setSelectedUserAccess(_user.access_list);
          setSaveDisabled(true);
        } else {
          setIsNewUser(true);
        }

        // User role list call
        const _user_roles = await session.query("admin/get_roles");
        setUserRoles(_user_roles);
        updateRoleOptionsNoAdmin();

        // Access list call
        const _access_lists = await session.query("companies/access_lists");
        setAccessLists(_access_lists);

        // User data permissions call
        const _perms = await session.query("reference/permissions/data");
        setAllDataPermissions(_perms.map((v) => {
          return { ...v, value: "" };
        }));
      }
      catch (error) {
        console.log("REJECTED", JSON.stringify(error));
        setFailToLoadUser(true);
      }
      setSaveDisabled(true);
      setIsLoading(false);
    }

    fetchAllUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State changes and error checking
  useEffect(() => {
    function checkIfValuesChanged() {
      if(!Object.keys(prevUserInfo).length) { return false }
      if(!isEqual(prevUserInfo, user)) {
        return true;
      } else if(permissionsHaveChanged()) {
        return true;
      } else {
        return false;
      }
    }
  
    function checkIfValuesAreEmpty() {
      if (
        user.first_name.length === 0 ||
        user.last_name.length === 0 ||
        user.email.length === 0 ||
        !isValidEmail ||
        (user.is_superuser === false && user.access_list_id.length === 0) || !user.role) {
        return true;
      } else {
        return false;
      }
    }

    // returns true when permissions have changes
    function permissionsHaveChanged() {
      // if lengths are different: permissions have changed
      if(selectedDataPermissions.length !== prevUserInfo.data_permissions.length) {
        return true;
      }

      let selectedIds = selectedDataPermissions.map((p) => p.id);
      let prevIds = prevUserInfo.data_permissions.map((p) => p.data_permission_id);
      // local data structure differs from api data structure: check only ids
      if(!isEqual(selectedIds, prevIds)) {
        return true
      } else {
        return false;
      }
    }

    if(checkIfValuesAreEmpty()) {
      setSaveDisabled(true);
    } else {
      setSaveDisabled(false);
    }

    const user_id = props.args.user_id;
    if (user_id) {
      setSaveDisabled(true);
      if(checkIfValuesChanged()) {
        setSaveDisabled(false);
        if(checkIfValuesAreEmpty()) {
          setSaveDisabled(true);
        }
      } 
    } 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user.first_name,
    user.last_name,
    user.email,
    user.is_superuser,
    user.is_disabled,
    user.access_list_id,
    user.role,
    user.data_permissions,
    isValidEmail,
    selectedDataPermissions,
    prevUserInfo
  ]);

  useEffect(() => { /* validate email format */
    if(!props.args.user_id && user.email.length > 0) {
      if( !validateEmail(user.email) ) { 
        setShowEmailMessage(true);
        setIsValidEmail(false);
      } else {
        setShowEmailMessage(false);
        setIsValidEmail(true);
      }
    } else {
      setShowEmailMessage(false);
      setIsValidEmail(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.email])

  useEffect(() => {
    const user_id = props.args.user_id;
    if (user_id) {
      setTimeout(() => {
        setSaveDisabled(true);
      }, 200);
    }
  }, [props.args.user_id]);

  function onClickSave(ev) {
    ev.stopPropagation();
    ev.preventDefault();

    // Trim all input fields before saving. 
    user.first_name = user.first_name.trim();
    user.last_name = user.last_name.trim();
    user.email = user.email.trim();

    const newErrors = {};
    if (!user.email) newErrors.email = "Required";
    if (!user.first_name) newErrors.first_name = "Required";
    if (!user.last_name) newErrors.last_name = "Required";
    if (!user.is_superuser && !user.access_list_id) newErrors.accessListId = "Required";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }
    setSaveDisabled(true);

    const newUser = {
      ...user,
      data_permissions: selectedDataPermissions.map((p) => {
        return {
          data_permission_id: p.id,
          value: p.value,
        };
      }),
    };

    if (!!newUser.id) {
      session
        .command("users/update", newUser, {}, { defaultErrorHandling: false })
        .then((response) => {
          setAllowNavigateAway(true);
          setShowSuccess(true);
          setUser(response);
        })
        .then(() => {
          setTimeout(() => {
            setShowSuccess(false);
            history.push("/admin-users");
          }, 2000);
        })
        .catch((error) => {
          console.log("User Update Failed", JSON.stringify(error));
        });
    } else {
      session
        .command("users/create", newUser, {}, { defaultErrorHandling: false })
        .then((response) => {
          setAllowNavigateAway(true);
          setShowSuccess(true);
          setUser(response);
        })
        .then(() => {
          setTimeout(() => {
            setShowSuccess(false);
            history.push("/admin-users");
          }, 2000);
        })
        .catch((error) => {
          console.log("User Create Failed", JSON.stringify(error));
        });
    }
  }

  const removeSpecial = /[^a-zA-Z-'ñáéíóúü ]+/g; // only allows letters, space, ', and -
  const removeExtraSpaces = /\s+/g; // if user types multiple spaces, trim to just one space

  function cleanUpInput(str) {
    return str.replace(removeSpecial, "").replace(removeExtraSpaces, " ");
  }

  function setFirstName(ev) {
    setUser({ ...user, first_name: cleanUpInput(ev.target.value) });
    if(ev.target.value.match(removeSpecial)) {
      setShowFirstNameMessage(true);
    }
    if (ev.target.value) {
      setErrors((prev) => delete prev.first_name);
    }
  }

  function setLastName(ev) {
    setUser({ ...user, last_name: cleanUpInput(ev.target.value) });
    if(ev.target.value.match(removeSpecial)) {
      setShowLastNameMessage(true);
    }
    if (ev.target.value) {
      setErrors((prev) => delete prev.last_name);
    }
  }

  function setEmail(ev) {
    setUser({ ...user, email: ev.target.value.trim() });
    if (ev.target.value) {
      setErrors((prev) => delete prev.email);
    }
  }

  function setSuperUser(ev) {
    setUser({ ...user, access_list_id: "", is_superuser: ev.target.checked });
    // unset all previous access list
    if(ev.target.checked) {
      setSelectedUserAccessID(0);
      setSelectedUserAccess("");
    }
  }

  function setEnabled(ev) {
    setUser({...user, is_disabled: ev.target.checked});
    if (ev.target.value) {
      setErrors((prev) => delete prev.is_disabled);
    }
  }

  const goBack = () => {
    history.push("/admin-users");
  };

  const onEmailHover = () => {
    if(disableEmail) {
      setShowEmailPopover(!showEmailPopover);
    }
  }

  // For User Role Dropdown Open/Close
  function toggleUserRoleDropDown() {
      setSelectedUserRoleIsOpen((prevState) => !prevState);
  }

  // Handler for User Role Dropdown selection
  function setSelectedUserRoleValue(ev) {
    if (typeof (ev.target.value) !== "undefined" && ev.target.value !== "none") {
      setSelectedUserRoleID(ev.target.id);
      setSelectedUserRole(ev.target.value);
      setUser({ ...user, role: { id: parseInt(ev.target.id), name: ev.target.value } });
    } else {
      setSelectedUserRoleID(0);
      setSelectedUserRole("");
      setUser(prevState => {
        const { role, ...newState } = prevState;
        return newState;
      });
    }
  }

  // For Access List Dropdown Open/Close
  function toggleAccessListDropDown() {
      setSelectedUserAccessIsOpen((prevState) => !prevState);
  }

  // Handler for Access List Dropdown selection
  function setSelectedUserAccessValue(ev) {
    if (typeof (ev.target.value) !== "undefined" && ev.target.value !== "none") {
      setSelectedUserAccessID(ev.target.id);
      setSelectedUserAccess(ev.target.value);
      setUser({ ...user, access_list_id: parseInt(ev.target.id), access_list: ev.target.value });
    } else {
      setSelectedUserAccessID(null);
      setSelectedUserAccess(null);
      setUser(prevState => {
        const { access_list, ...newState } = prevState;
        return {
          ...newState,
          access_list_id: ""
        };
      });
    }
  }

  // show errors when required fields are missing
  useEffect(() => {
    const newErrors = {};
    if (!user.email) newErrors.email = "Required";
    if (!user.first_name) newErrors.first_name = "Required";
    if (!user.last_name) newErrors.last_name = "Required";
    if (!user.is_superuser && !user.access_list_id) newErrors.accessListId = "Required";
    setErrors(newErrors);
  }, [user])

  /*
  * check the email domain: 
  * only speedeondata.com email is allowed 'administrator' or 'all company access'
  */
  useEffect(() => {
    if(user.email.length > 2) {
      let email = user.email.split('@');
      let isSpeedeon = false;
      if(email.length > 1) {
        isSpeedeon = email[1].toLowerCase().includes('speedeondata.com');
      }    
      
      if(!isSpeedeon){
        setHasInternalEmail(false);
        if(props.args.user_id) {
          setUser({ ...user, is_superuser: false });
        } else {
          if(user.role && user.role.name === "Administrator") {
            setSelectedUserRoleID(12);
            setSelectedUserRole("View Only");
            setUser({ ...user, role: {id: 12, name: "View Only"}, is_superuser: false });
          }
        }
      } else {
        setHasInternalEmail(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.email])

  /* check all roles and remove the options with 'administrator' permissions */
  const updateRoleOptionsNoAdmin = () => {
    let roles_without_admin = [];
    session
    .query("admin/get_roles")
    .then((response) => {
      response.forEach((val) => {
        let has_admin = false
        val.permissions.forEach((perm) => {
          if(perm.name === 'administration') {
            has_admin = true;
          }
        });
        if (!has_admin) {
          roles_without_admin.push(val);
        }
      })
    })
    .catch((error) => { console.log(JSON.stringify(error)) })
    .finally(() => {
      setNoAdminUserRoles(roles_without_admin)
    })
  }

  /* handle reset MFA for the user */
  const onClickResetMfa = () => {
    setShowResetMfaModal(true);
  }

  const handleResetMfa = () => {
    session.command("users/reset_mfa", user, {}, { defaultErrorHandling: false })
      .then(() => {
        setDisableMfaButton(true);
        setShowMfaSuccess(true);
        setTimeout(() => {
          setShowMfaSuccess(false);
        }, 2500);
      })
      .catch((error) => {
        console.log("Fail to reset MFA ", JSON.stringify(error))
      })
    toggleMfaModal();
  }

  const toggleMfaModal = () => setShowResetMfaModal(!showResetMfaModal);

  useEffect(() => {
    let current_data_permissions = selectedDataPermissions.map((p) => {
      return {
        data_permission_id: p.id,
        value: p.value,
      };
    });
    if(!isEqual(current_data_permissions, user.data_permissions)) {
      setUser({...user, data_permissions: current_data_permissions});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDataPermissions])

  useEffect(() => {
    const _checkBeforeLeave = () => {
      if(props.args.user_id) {
        // editing user
        if(saveDisabled) {
          // no changes
          setHasUnsavedChanges(false);
        } else {
          // has changed
          setHasUnsavedChanges(true);
        }
      } else {
        // creating new user
        if(!isEqual(user, user_init)) {
          // has changed
          setHasUnsavedChanges(true);
        } else {
          // no changes
          setHasUnsavedChanges(false);
        }
      }
    }
    
    if(!allowNavigateAway) {
      _checkBeforeLeave();
    } else {
      setHasUnsavedChanges(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveDisabled, user])

  if(isLoading) {
    return (
      <div className="loader-container">
        <ProgressLoader />
      </div>
    )
  }

  /**
   * show this if the user could not be loaded
   */
  if(failToLoadUser) {
    return (
      <Form className="page user-form-page" onSubmit={e => e.preventDefault()}>
      <div className="list-header sticky-page-header">
        <button type="button" className="new-button" onClick={goBack}>
          <i className="bi bi-chevron-left"></i>{" "} Users
        </button>
        <h1>User Details</h1>
        <i className="spacer" />
      </div>
      <section className="user-form-details mt-5">
        <div>The user details could not be loaded.</div>
      </section>
      </Form>
    )
  }

  return (<>
    <Form className="page user-form-page" onSubmit={e => e.preventDefault()}>
      <div className="list-header sticky-page-header">
        <button type="button" className="new-button" onClick={goBack}>
          <i className="bi bi-chevron-left"></i>{" "} Users
        </button>
        <h1>User Details</h1>
        <i className="spacer" />
      </div>

      <section className="user-form-details mt-5">
        <div className="user-form-buttons text-end">
          {user.id && (
            <div className="mb-3">
              <Button className="save-button" onClick={onClickResetMfa} disabled={disableMfaButton}>
                Reset Multi-Factor Authentication
              </Button>
            </div>
          )}
          <div>
            <Button className="save-button promote" onClick={onClickSave} disabled={saveDisabled}>
              Save Changes
            </Button>
          </div>
        </div>

        <Field
          label="* First Name"
          className="user-form-details-heading"
          error={errors.first_name}
        >
          <input
            type="text"
            value={user.first_name}
            className="user-form-details-info"
            onChange={setFirstName}
            onMouseEnter={() => {if(showFirstNameMessage) setShowFirstNameMessage(false)}}
          />
          <HelpMessage message="*Special characters are not allowed." show={showFirstNameMessage} />
        </Field>

        <Field
          label="* Last Name"
          className="user-form-details-heading"
          error={errors.last_name}
        >
          <input
            type="text"
            value={user.last_name}
            className="user-form-details-info"
            onChange={setLastName}
            onMouseEnter={() => {if(showLastNameMessage) setShowLastNameMessage(false)}}
          />
          <HelpMessage message="*Special characters are not allowed." show={showLastNameMessage} />
        </Field>

        {
          props.args.user_id ? (<>
            <Field
              label="* Email Address"
              className="user-form-details-heading"
              error={errors.email}
            >
              <span id="email-tooltip">
                <input
                  type="text"
                  value={user.email}
                  className="user-form-details-info not-allowed"
                  readOnly
                />
              </span>
            </Field>
            <Popover
              flip
              fade
              trigger="hover"
              onMouseOut={onEmailHover}
              target="email-tooltip"
              isOpen={showEmailPopover}
              toggle={onEmailHover}
            >
              <PopoverBody>
                Emails Cannot Be Modified. Please create a new user if an email is incorrect.
              </PopoverBody>
            </Popover>
          </>) : (
            <Field
              label="* Email Address"
              className="user-form-details-heading"
              error={errors.email}
            >
              <input
                type="text"
                value={user.email}
                className="user-form-details-info"
                onChange={setEmail}
              />
              <HelpMessage message="*Please enter a valid email." show={showEmailMessage} />
            </Field>
          )
        }


      
        {session.appState.identity.user.is_superuser && (
          <>
           <FormGroup row className='form-grp mt-3'>
                <Label for="user-role-selection">User Role:<sup>*</sup> {!user.role && <span className="form-field-error"> &nbsp; REQUIRED</span>}</Label>
                    <Dropdown
                        className="user-role-selection"
                        isOpen={selectUserRoleIsOpen}
                        toggle={toggleUserRoleDropDown}
                        id="user-role-selection"
                    >
                        <DropdownToggle size="sm" caret color="light">
                        { selectedUserRole || "Select Role"}
                        </DropdownToggle>
                        <DropdownMenu value={selectedUserRole}>
                          <DropdownItem
                            onClick={(e) => setSelectedUserRoleValue(e)}
                            key="none"
                            id={"0"}
                            value="none"
                          >
                            None
                          </DropdownItem>
                        {(hasInternalEmail ? userRoles : noAdminUserRoles).map((l, i) => (
                          <DropdownItem
                            onClick={(e) => {
                              setSelectedUserRoleValue(e);
                            }}
                            key={l.id.toString()}
                            id={l.id}
                            value={l.name}
                          >
                            {l.name}
                          </DropdownItem>
                        ))}
                        </DropdownMenu>
                    </Dropdown>
            </FormGroup>
            {hasInternalEmail && (
              <Field label="Internal Speedeon Employee - All Companies Access" className="user-form-details-heading">
                <input
                  type="checkbox"
                  checked={user.is_superuser}
                  onChange={setSuperUser}
                  className="user-form-details-info"
                  />
              </Field>
            )}
          </>
        )}

        {!user.is_superuser && (
          <FormGroup row className='form-grp mt-4'>
                <Label for="user-access-selection">Access List:<sup>*</sup> {!user.access_list_id && <span className="form-field-error"> &nbsp; REQUIRED</span>}</Label>
                    <Dropdown
                      className="user-access-selection"
                      isOpen={selectUserAccessIsOpen}
                      toggle={toggleAccessListDropDown}
                      id="user-access-selection"
                    >
                      <DropdownToggle size="sm" caret color="light">{selectedUserAccess || "Select Access"}</DropdownToggle>
                        <DropdownMenu value={selectedUserAccess}>
                          <DropdownItem
                            onClick={(e) => {
                              setSelectedUserAccessValue(e);
                            }}
                            key="none"
                            id="0"
                            value="none">
                            None
                          </DropdownItem>
                          {accessLists.map((l, i) => (
                            <DropdownItem
                              onClick={(e) => {
                                setSelectedUserAccessValue(e);
                              }}
                              key={l.id.toString()}
                              id={l.id}
                              value={l.name}
                            >
                              {l.name}
                            </DropdownItem>
                          ))}
                      </DropdownMenu>
                    </Dropdown>
          </FormGroup>
        )}

        {!isNewUser && 
          <Field label="Disable User Account" className="user-form-details-heading">
            <input
              type="checkbox"
              checked={user.is_disabled}
              onChange={setEnabled}
              className="user-form-details-info"
            />
          </Field>
        }

      </section>
      <DualSelect
        name={"Data Permissions"}
        allOptions={allDataPermissions}
        selectedOptions={selectedDataPermissions}
        setSelectedOptions={setSelectedDataPermissions}
        selectedOptionsRender={(o) => (
          <Field label={o.name} className="user-form-details-heading">
            <input
              type="text"
              value={o.value}
              className="user-form-details-info"
              onChange={(ev) => updateDataPermissionsValue(ev, o.id)}
            />
          </Field>
        )}
      />
      <ConfirmSuccess show={showSuccess} label="Saved!" />
      <ConfirmSuccess show={showMfaSuccess} label="MFA Reset Submitted!" />
      <UnsavedChanges hasUnsavedChanges={hasUnsavedChanges} />
    </Form>
    <Modal centered backdrop isOpen={showResetMfaModal}>
      <ModalHeader>
        Reset Multi-Factor Authentication
      </ModalHeader>
      <ModalBody>
        <Alert color="light" className="d-flex">
          <i className="bi bi-exclamation-octagon"></i>
          <div>
            Selecting <b>Continue</b> will Reset the Multi-Factor Authentication for the user: <br />
            <strong>{user.first_name} {user.last_name}</strong> <br />
            Are you sure you want to continue?
          </div>
        </Alert>
      </ModalBody>
      <ModalFooter>
        <Button className="promote" onClick={toggleMfaModal}>Cancel</Button>
        <Button onClick={handleResetMfa}>Continue</Button>
      </ModalFooter>
    </Modal>
  </>);
}

function HelpMessage({message, show}) {
  if(show) {
    return (
      <span style={{ textTransform: 'none', fontStyle: 'italic' }}>
        {message}
      </span>
    );
  }
  return <></>;
}